import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createTenant,
  getProvisioningRuns,
  patchProvisioningStep,
  startProvisioningRun,
} from '../../api/provisioning';
import { useEnvironment } from '../useEnvironment/useEnvironment';
import type {
  IProvisioningRun,
  IProvisionTenantInput,
  IStartRunInput,
  ProvisioningStepStatus,
} from '../../types';

export const isRunActive = (run: IProvisioningRun): boolean =>
  run.status !== 'succeeded' && run.status !== 'failed';

const ACTIVE_POLL_MS = 5000;

/** Runs for one tenant; polls every five seconds while any run is still with GitHub. */
export const useProvisioningRuns = (tenant: string) => {
  const { environment } = useEnvironment();
  return useQuery({
    queryKey: ['provisioning-runs', environment, tenant],
    queryFn: () => getProvisioningRuns(environment, tenant),
    select: ({ data }) => data,
    enabled: Boolean(tenant),
    refetchInterval: (data) => (data?.some(isRunActive) ? ACTIVE_POLL_MS : false),
  });
};

export const useProvisioningMutations = (tenant: string) => {
  const { environment } = useEnvironment();
  const client = useQueryClient();
  const refresh = () =>
    Promise.all([
      client.invalidateQueries({ queryKey: ['provisioning-runs', environment, tenant] }),
      client.invalidateQueries({ queryKey: ['tenant', environment, tenant] }),
      client.invalidateQueries({ queryKey: ['tenants', environment] }),
      client.invalidateQueries({ queryKey: ['tenant-audit', environment, tenant] }),
    ]);
  const start = useMutation({
    mutationFn: (input: IStartRunInput) => startProvisioningRun(environment, tenant, input),
    onSuccess: refresh,
  });
  const setStep = useMutation({
    mutationFn: ({
      runId,
      step,
      status,
    }: {
      runId: string;
      step: string;
      status: ProvisioningStepStatus;
    }) => patchProvisioningStep(environment, tenant, runId, step, status),
    onSuccess: refresh,
  });
  return { start, setStep };
};

export const useCreateTenant = () => {
  const { environment } = useEnvironment();
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: IProvisionTenantInput) => createTenant(environment, input),
    onSuccess: () => client.invalidateQueries({ queryKey: ['tenants', environment] }),
  });
};
